import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { DailyPoint } from '@/api/client';
import { fmtNum } from '@/lib/format';
import { mutedText } from '@/ui';

/**
 * Same treatment as the dashboard analytics chart
 * (bestyDashboard/.../components/bffAnalytics/MessageVolume.tsx): stacked gradient
 * areas, dashed grid, borderless axes, series rail with per-series totals.
 */
const SERIES = [
  { key: 'autopilot', label: 'Autopilot', color: '#2563eb' },
  { key: 'dashboard', label: 'Dashboard', color: '#0d9488' },
  { key: 'offers', label: 'Offers', color: '#d97706' },
] as const;

type SeriesKey = (typeof SERIES)[number]['key'];

function formatDay(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface TooltipEntry {
  name?: string;
  value?: number;
  color?: string;
}

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipEntry[]; label?: string }) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((sum, entry) => sum + (entry.value ?? 0), 0);
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3 shadow-lg">
      <p className="mb-2 text-xs font-medium text-neutral-900">{formatDay(String(label))}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-neutral-500">{entry.name}</span>
          </div>
          <span className="font-medium text-neutral-900">{fmtNum(entry.value ?? 0)}</span>
        </div>
      ))}
      <div className="mt-2 flex items-center justify-between border-t border-neutral-100 pt-2 text-xs font-medium">
        <span className="text-neutral-500">Total</span>
        <span className="text-neutral-900">{fmtNum(total)}</span>
      </div>
    </div>
  );
}

export function MessageVolumeChart({ daily }: { daily: DailyPoint[] }) {
  if (daily.length === 0) {
    return <div className={`p-4 ${mutedText}`}>No messages in this date range.</div>;
  }

  const seriesTotal = (key: SeriesKey) => daily.reduce((sum, day) => sum + day[key], 0);
  const total = SERIES.reduce((sum, s) => sum + seriesTotal(s.key), 0);

  return (
    <div className="flex flex-col gap-4 p-4 lg:flex-row">
      <div className="h-72 min-w-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={daily} margin={{ top: 20, right: 30, left: 10, bottom: 10 }}>
            <defs>
              {SERIES.map((s) => (
                <linearGradient key={s.key} id={`skyrun-area-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#ebebeb" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatDay}
              stroke="#a3a3a3"
              fontSize={11}
              tickLine={false}
              axisLine={false}
            />
            <YAxis stroke="#a3a3a3" fontSize={11} tickLine={false} axisLine={false} tickFormatter={fmtNum} />
            <Tooltip content={<ChartTooltip />} />
            {SERIES.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={1.75}
                fill={`url(#skyrun-area-${s.key})`}
                dot={false}
                activeDot={{ r: 4, fill: s.color, stroke: '#ffffff', strokeWidth: 2 }}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="w-full shrink-0 lg:w-52 lg:border-l lg:border-neutral-200 lg:pl-4">
        <ul className="space-y-1">
          {SERIES.map((s) => (
            <li key={s.key} className="flex items-center justify-between px-2 py-1 text-xs text-neutral-700">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} aria-hidden />
                <span>{s.label}</span>
              </div>
              <span className="font-medium tabular-nums">{fmtNum(seriesTotal(s.key))}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 border-t border-neutral-100 px-2 pt-3">
          <div className="text-xs font-semibold text-neutral-900">Total messages</div>
          <div className="mt-1 text-xs font-medium tabular-nums text-neutral-500">{fmtNum(total)}</div>
        </div>
      </div>
    </div>
  );
}
